// services/commentService.js
const API_BASE = `${process.env.REACT_APP_API_URL}/pdfs`;

// Helper function to get auth headers
const getAuthHeaders = () => {
  const token = sessionStorage.getItem('token');
  if (!token) {
    console.error('❌ No authentication token found');
    throw new Error('Please log in to access comments');
  }
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

// Helper function to handle auth errors
const handleAuthError = (error) => {
  if (error.response && error.response.status === 401) {
    console.error('❌ Authentication failed - redirecting to login');
    localStorage.removeItem('token');
    window.location.href = '/login';
    throw new Error('Session expired. Please log in again.');
  }
  throw error;
};

export const commentService = {
  async getComments(title) {
    try {
      console.log('🔍 Fetching comments for:', title);
      const url = `${API_BASE}/${title}/comments`;
      console.log('📡 Making request to:', url);
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });
      
      console.log('📡 Response status:', response.status);
      
      if (response.status === 401) {
        // Token expired or invalid
        console.error('❌ Authentication failed - please log in again');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return [];
      }
      
      if (response.status === 404) {
        console.log('📄 No PDF found with this title for current user');
        return [];
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Comments fetch response:', data);
      
      return data.success ? data.comments : [];
    } catch (error) {
      console.error('❌ Error fetching comments:', error);
      
      if (error.message.includes('401')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      return [];
    }
  },

  async saveComments(title, comments) {
    try {
      console.log('💾 Saving comments:', {
        title,
        commentsCount: comments.length,
      });

      const url = `${API_BASE}/${title}/comments`;
      console.log('📡 Making request to:', url);

      const response = await fetch(url, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ comments }),
      });
      
      console.log('📡 Save response status:', response.status);
      
      if (response.status === 401) {
        console.error('❌ Authentication failed - please log in again');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return false;
      }
      
      if (response.status === 404) {
        console.error('❌ PDF not found for current user');
        return false;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Save comments response:', data);
      
      return data.success;
    } catch (error) {
      console.error('❌ Error saving comments:', error);
      
      if (error.message.includes('401')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      return false;
    }
  },

  async addComments(title, comments) {
    try {
      console.log('➕ Adding comments:', {
        title,
        commentsCount: comments.length,
      });

      const url = `${API_BASE}/${title}/comments`;
      console.log('📡 Making PATCH request to:', url);

      const response = await fetch(url, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify({ comments }),
      });
      
      console.log('📡 PATCH response status:', response.status);
      
      if (response.status === 401) {
        console.error('❌ Authentication failed - please log in again');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return false;
      }
      
      if (response.status === 404) {
        console.error('❌ PDF not found for current user');
        return false;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Add comments response:', data);
      
      return data.success;
    } catch (error) {
      console.error('❌ Error adding comments:', error);
      
      if (error.message.includes('401')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      return false;
    }
  },

  async deleteComment(title, commentId) {
    try {
      console.log('🗑️ Deleting comment:', {
        title,
        commentId,
      });

      const url = `${API_BASE}/${title}/comments/${commentId}`;
      console.log('📡 Making DELETE request to:', url);

      const response = await fetch(url, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      
      console.log('📡 DELETE response status:', response.status);
      
      if (response.status === 401) {
        console.error('❌ Authentication failed - please log in again');
        localStorage.removeItem('token');
        window.location.href = '/login';
        return false;
      }
      
      if (response.status === 404) {
        console.error('❌ PDF or comment not found for current user');
        return false;
      }
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      console.log('✅ Delete comment response:', data);
      
      return data.success;
    } catch (error) {
      console.error('❌ Error deleting comment:', error);
      
      if (error.message.includes('401')) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
      
      return false;
    }
  }
};